'use client';

import {
  type AddressData,
  type AddressFormInputValidation,
  AddressFormV3,
  type AddressFormV3Props,
  FieldName,
  mergeValidationConfigs,
} from '@jouzen/ecom-components/v3/AddressForm';
import {
  FormControl,
  type FormControlChangeEvent,
} from '@jouzen/ecom-components/v3/FormControl';
import { cx } from 'class-variance-authority';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import { useTranslations } from 'next-intl';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type CountryData } from '@/app/hooks/useCountryData';
import useFocusFirstErrorComponent from '@/app/hooks/useFocusFirstErrorComponent';
import { logger as browserLogger } from '@/logger/logger.browser';
import { type AddressFormValidation } from '@/queries/types/graphql';

import { transformValidationData } from './utils';

export interface AddressDataWithAttention extends AddressData {
  attention?: string;
}

export interface AddressFormProps extends Omit<
  AddressFormV3Props,
  'className' | 'onFormSubmit'
> {
  readonly addressFormValidation: AddressFormValidation;
  readonly className?: string; // used on this wrapper element
  readonly countryData: CountryData;
  readonly formClassName?: AddressFormV3Props['className']; // passed down to form
  readonly loading?: boolean;
  readonly onFormSubmit?: (values: AddressDataWithAttention) => void;
  readonly setSelectedCountryCode: Dispatch<SetStateAction<string>>;
}

const AddressForm = ({
  addressFormValidation,
  className = '',
  countryData,
  excludedFields: initExcludedFields = [],
  fieldProps: fieldPropsOverrides = {},
  formClassName = '',
  hiddenFields = [],
  initialValues = {},
  inputValidation: inputValidationOverrides,
  loading: isLoading = false,
  selectedCountryCode,
  setSelectedCountryCode,
  submitButton,
  onFormSubmit = noop,
  onValidationError = noop,
  ...formProps
}: AddressFormProps): JSX.Element => {
  const t = useTranslations();
  const formUuid = useId();
  const formId = formProps.id ?? `address-form-${formUuid}`;

  const [attention, setAttention] = useState<string>(
    (initialValues as { attention?: string })?.attention ?? '',
  );

  useEffect(() => {
    const next = (initialValues as { attention?: string })?.attention ?? '';
    setAttention((prev) => (prev !== next ? next : prev));
  }, [(initialValues as { attention?: string })?.attention]);

  // focus on first error, on validation
  const formRef = useRef<HTMLDivElement>(null);
  const checkoutFocusFirstErrorComponent = useFocusFirstErrorComponent(formRef);

  const excludedFields = useMemo(
    () =>
      countryData.hasRegions
        ? initExcludedFields
        : [...initExcludedFields, FieldName.State],
    [countryData.hasRegions, initExcludedFields],
  );

  const fieldProps = useMemo(
    () =>
      merge({}, fieldPropsOverrides, {
        [FieldName.State]: {
          options: countryData.regionOptions,
        },
        [FieldName.Country]: {
          onChange: (evt: FormControlChangeEvent) => {
            fieldPropsOverrides[FieldName.Country]?.onChange?.(evt);
            setSelectedCountryCode(evt.target.value);
          },
          options: countryData.countryOptions,
        },
      }),
    [
      countryData.countryOptions,
      countryData.regionOptions,
      fieldPropsOverrides,
      setSelectedCountryCode,
    ],
  );

  const inputValidation = useMemo(() => {
    const countryValidation: AddressFormInputValidation =
      transformValidationData(addressFormValidation);

    // merge country data from server with overrides, using our custom fn
    const config: AddressFormInputValidation = mergeValidationConfigs(
      countryValidation,
      inputValidationOverrides as unknown as Partial<AddressFormInputValidation>,
    );

    return config;
  }, [addressFormValidation, inputValidationOverrides]);

  const handleValidationError: AddressFormV3Props['onValidationError'] = (
    errors,
    values,
  ) => {
    // Fire callback
    onValidationError(errors, values);

    // Focus on first invalid field
    checkoutFocusFirstErrorComponent();
  };

  const handleInvalidFields = (invalidFields: FieldName[]) => {
    browserLogger.warn('All hidden fields must provide valid initialValues', {
      invalidFields,
    });
  };

  const handleFormSubmit = (values: Parameters<typeof onFormSubmit>[0]) => {
    onFormSubmit({ ...values, attention: attention || undefined });
  };

  return (
    <div ref={formRef} className={cx(className)}>
      <div
        data-testid="field-attention"
        data-cy="field-attention"
        className="mb-4"
      >
        <FormControl
          control="TextInput"
          name="attention"
          label={t('address_form_attention')}
          value={attention}
          onChange={(e: FormControlChangeEvent) =>
            setAttention(e.target.value ?? '')
          }
          className="w-full"
        />
      </div>
      <AddressFormV3
        key={formId} // updating key forces re-render of form, forcing an update of initialValues
        id={formId}
        noValidate
        {...formProps}
        className={formClassName}
        excludedFields={excludedFields}
        fieldProps={fieldProps}
        hiddenFields={hiddenFields}
        initialValues={{
          ...initialValues,
          country: selectedCountryCode,
        }}
        inputValidation={inputValidation}
        loading={isLoading}
        onFormSubmit={handleFormSubmit}
        onInvalidFields={handleInvalidFields}
        onValidationError={handleValidationError}
        selectedCountryCode={selectedCountryCode}
        submitButton={submitButton}
      />
    </div>
  );
};

export default AddressForm;
